/**
 * Envío AP-14 · aviso en los nodos de carga cuando la imagen o el video no
 * cumplen los límites de ENTRADA del modelo al que están conectados, con un
 * botón «Adaptar» que abre la herramienta con el ajuste correcto ya elegido.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, Scaling } from 'lucide-react';
import { useWorkflowStore } from '../../store/workflowStore';
import { firstLimitIssue } from '../../utils/inputLimits';

const cache = new Map();

async function headBytes(url) {
  try {
    const r = await fetch(url, { method: 'HEAD' });
    const n = Number(r.headers.get('content-length'));
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch (_e) { return null; }
}

function probeImage(url) {
  return new Promise((resolve) => {
    const im = new Image();
    im.onload = () => resolve({ width: im.naturalWidth, height: im.naturalHeight });
    im.onerror = () => resolve({});
    im.src = url;
  });
}

function probeVideo(url) {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata'; v.muted = true;
    v.onloadedmetadata = () => resolve({ duration: Number.isFinite(v.duration) ? v.duration : null, width: v.videoWidth, height: v.videoHeight });
    v.onerror = () => resolve({});
    v.src = url;
  });
}

export async function mediaInfo(url, kind) {
  if (!url || url.startsWith('blob:')) return null;
  const key = `${kind}:${url}`;
  if (!cache.has(key)) {
    cache.set(key, Promise.all([kind === 'video' ? probeVideo(url) : probeImage(url), headBytes(url)])
      .then(([m, bytes]) => ({ ...m, bytes })));
  }
  return cache.get(key);
}

export default function NodeLimitWarning({ nodeId, url, kind, onAdapt }) {
  // destinos conectados como texto estable (evita re-render por arrays nuevos)
  const targetsKey = useWorkflowStore((s) => s.edges
    .filter((e) => e.source === nodeId)
    .map((e) => `${s.nodes.find((n) => n.id === e.target)?.type || ''}:${e.targetHandle || ''}`)
    .join('|'));
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!targetsKey || !url) { setInfo(null); return undefined; }
    mediaInfo(url, kind).then((i) => { if (alive) setInfo(i); });
    return () => { alive = false; };
  }, [url, kind, targetsKey]);

  if (!info || !targetsKey) return null;
  const targets = targetsKey.split('|').map((t) => { const [type, handle] = t.split(':'); return { type, handle }; });
  const issue = firstLimitIssue(info, targets);
  if (!issue) return null;
  const { spec, problems } = issue;
  return (
    <div className="node-limit-warn nodrag" role="note" data-testid="node-limit-warn">
      <AlertTriangle size={12} aria-hidden="true" />
      <span>
        <b>{spec.label}:</b> {problems.join(' · ')}.
        {spec.autoFix ? ' Se ajusta sola al ejecutar.' : ' Adaptalo antes de ejecutar.'}
      </span>
      <button type="button" className="node-limit-btn nodrag" onClick={() => onAdapt?.(spec.preset)} data-testid="node-limit-adapt">
        <Scaling size={11} aria-hidden="true" /> Adaptar
      </button>
    </div>
  );
}
